import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  SupportButton,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { toastApiRef, useApi } from '@backstage/frontend-plugin-api';

import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SendIcon from '@material-ui/icons/Send';

import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';

import { CreateDateTimePickerComponent } from './CreateDateTimePickerComponent.tsx';
import { PreviewNotificationComponent } from './PreviewNotificationComponent.tsx';
import { createTitleAndDescription, formatDateTime } from './utils.ts';
import { Category, Status } from './types.ts';
import { GettingStartedComponent } from './GettingStartedComponent.tsx';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { frontendComponentNotificationPermission } from '@zeiss/backstage-plugin-frontend-permissions-common';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

const DEFAULT_CATEGORY = Category.Incident;
const DEFAULT_STATUS = Status.Up;
const DEFAULT_STATUS_MAINTENANCE = Status.Announcement;

export const CreateNotificationComponent = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instance, setInstance] = useState('');
  const [category, setCategory] = useState<Category>(DEFAULT_CATEGORY);
  const [status, setStatus] = useState<Status>(DEFAULT_STATUS);
  const [statusIoNotification, setStatusIoNotification] =
    useState<boolean>(false);
  const [statusIoAnnotationPresent, setStatusIoAnnotationPresent] =
    useState<boolean>(false);
  const [authorName, setAuthorName] = useState('');
  const [userEntity, setUserEntity] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const dateFns = new DateFnsUtils();

  const navigate = useNavigate();
  const { namespace, kind, name } = useParams();
  const toastApi = useApi(toastApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const getUser = useCallback(async () => {
    const { userEntityRef = '' } = await identityApi.getBackstageIdentity();
    const { displayName = '' } = await identityApi.getProfileInfo();

    setAuthorName(displayName);
    setUserEntity(userEntityRef);
  }, [identityApi]);

  useEffect(() => {
    getUser().then();
  }, [getUser]);

  const annotationPresent = useCallback(
    async (
      entityKind: string | undefined,
      entityNamespace: string | undefined,
      entityName: string | undefined,
    ) => {
      if (!entityKind || !entityNamespace || !entityName) {
        return false;
      }
      const entity = await catalogApi.getEntityByRef({
        kind: entityKind,
        namespace: entityNamespace,
        name: entityName,
      });
      const annotations = entity?.metadata?.annotations;

      // if entity is a System then only component id is necessary
      if (entityKind === 'system') {
        const areStatusIoAnnotationsPresent =
          annotations?.hasOwnProperty('zeiss.status.io/component-id');
        return areStatusIoAnnotationsPresent || false;
      }

      const areStatusIoAnnotationsPresent =
        annotations?.hasOwnProperty('zeiss.status.io/component-id') &&
        annotations?.hasOwnProperty('zeiss.status.io/container-id');
      return areStatusIoAnnotationsPresent || false;
    },
    [catalogApi],
  );

  useEffect(() => {
    annotationPresent(kind, namespace, name).then(isAnnotationPresent =>
      setStatusIoAnnotationPresent(isAnnotationPresent),
    );
  }, [kind, namespace, name, annotationPresent]);

  const goBack = () => {
    navigate(-1);
  };

  const handleOnTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const handleInstanceChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInstance(event.target.value);
  };

  const handleCategoryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCategory(value as Category);

    if (value === Category.Maintenance) {
      setStatus(DEFAULT_STATUS_MAINTENANCE);
    }

    if (value === Category.Incident || value === Category.SecurityIncident) {
      setStatus(DEFAULT_STATUS);
    }
  };

  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setStatus(value as Status);
  };

  const handleStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);

    if (!isNaN(date.getTime())) {
      if (!endDate || date <= endDate) {
        setStartDate(date);
      } else {
        setStartDate(startDate);
        // eslint-disable-next-line no-alert
        alert('Start date cannot be after than end date.');
      }
    } else {
      setStartDate(null);
    }
  };

  const handleEndDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);

    if (!isNaN(date.getTime())) {
      if (!startDate || date >= startDate) {
        setEndDate(date);
      } else {
        setEndDate(endDate);
        // eslint-disable-next-line no-alert
        alert('The end date cannot be before the start date.');
      }
    } else {
      setEndDate(null);
    }
  };

  const handleStatusIOCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setStatusIoNotification(event.target.checked);
  };

  async function sendBackstageNotification(
    previewTitle: string,
    previewDescription: string,
  ) {
    const notificationPluginUrl = await discoveryApi.getBaseUrl(
      'zeiss-notifications',
    );
    if (kind===undefined || namespace===undefined || name===undefined){
      toastApi.post({
        title: 'Portal Notification Sending Failed',
        status: 'danger',
      });
      return;
    }
    const response = await fetchApi
      .fetch(`${notificationPluginUrl}/notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sourceEntity: stringifyEntityRef({ kind, namespace, name }),
          title: previewTitle,
          description: previewDescription,
          category,
          userEntity,
        }),
      })
      .catch(error => {
        throw new Error(`Error while posting data: ${error}`);
      });

    if (response.status === 201) {
      toastApi.post({
        title: 'Portal Notification Sent Successfully',
        status: 'success',
      });
    } else {
      toastApi.post({
        title: 'Portal Notification Sending Failed',
        status: 'danger',
      });
    }
  }

  async function sendStatusIoNotification(
    previewTitle: string,
    previewDescription: string,
  ) {
    const statusIoPluginUrl = await discoveryApi.getBaseUrl('statusio');
    if (kind===undefined || namespace===undefined || name===undefined){
      toastApi.post({
        title: 'status.io Notification Sending Failed',
        status: 'danger',
      });
      return;
    }

    const response = await fetchApi
      .fetch(`${statusIoPluginUrl}/notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          catalogEntity: stringifyEntityRef({ kind, namespace, name }),
          category,
          status,
          title: previewTitle,
          description: previewDescription,
          startDate,
          endDate,
        }),
      })
      .catch(error => {
        throw new Error(`Error while posting data: ${error}`);
      });

    if (response.status === 201) {
      toastApi.post({
        title: 'status.io Notification Sent Successfully',
        status: 'success',
      });
    } else {
      toastApi.post({
        title: 'status.io Notification Sending Failed',
        status: 'danger',
      });
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      category === Category.Maintenance &&
      (startDate === null || endDate === null)
    ) {
      // eslint-disable-next-line no-alert
      alert('Set start and end date.');
      return;
    }

    const { notificationTitle, notificationDescription } =
      createTitleAndDescription({
        category,
        name,
        status,
        title,
        authorName,
        description,
        instance,
        startDate,
        endDate,
        dateFns,
      });
    await sendBackstageNotification(notificationTitle, notificationDescription);

    if (statusIoNotification) {
      await sendStatusIoNotification(
        notificationTitle,
        notificationDescription,
      );
    }

    goBack();
  };

  const shouldStatusIOCheckboxBeDisplayed =
    statusIoAnnotationPresent &&
    (([Category.Incident, Category.SecurityIncident].includes(category) &&
      [Status.Down, Status.Degraded].includes(status)) ||
      (category === Category.Maintenance && status === Status.Announcement));

  return (
    <RequirePermission
      permission={frontendComponentNotificationPermission}
      resourceRef={`${kind}:${namespace}/${name}`}
    >
      <Page themeId="tool">
        <Header
          title={`Create Notification | ${name}`}
          subtitle="Notify all consumer of the APIs this component provides"
        />
        <Content>
          <ContentHeader>
            <SupportButton />
          </ContentHeader>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Card>
                <CardContent>
                  <Box display="flex" flexDirection="column" gridGap={4}>
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={3} direction="column">
                        <Grid item xs={12}>
                          <TextField
                            required
                            id="title"
                            label="Title"
                            variant="filled"
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            onChange={handleOnTitleChange}
                            value={title}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            required
                            id="description"
                            label="Description"
                            placeholder="Describe which Users are impacted.Are all Users impacted or only a subset of users? Describe are all features impacted or a subset of features?"
                            multiline
                            minRows={6}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            fullWidth
                            variant="filled"
                            inputProps={{ maxLength: 5000 }}
                            onChange={handleDescriptionChange}
                            value={description}
                          />
                        </Grid>

                        {category !== 'Update' && (
                          <Grid item xs={12}>
                            <TextField
                              required
                              id="instance"
                              label="Instance"
                              placeholder="Where is the impact? Is it a global issue or only in a Region?"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              variant="filled"
                              fullWidth
                              inputProps={{ maxLength: 100 }}
                              onChange={handleInstanceChange}
                              value={instance}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">Category</FormLabel>
                            <RadioGroup
                              row
                              id="category"
                              name="category"
                              value={category}
                              color="default"
                              onChange={handleCategoryChange}
                            >
                              <FormControlLabel
                                value={Category.Incident}
                                control={<Radio required />}
                                label="Incident"
                              />
                              <FormControlLabel
                                value={Category.SecurityIncident}
                                control={<Radio required />}
                                label="Security Incident"
                              />
                              <FormControlLabel
                                value={Category.Maintenance}
                                control={<Radio required />}
                                label="Maintenance"
                              />
                              <FormControlLabel
                                value={Category.Update}
                                control={<Radio required />}
                                label="Update"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        {category === Category.Maintenance && (
                          <CreateDateTimePickerComponent
                            data={{
                              status,
                              startDate: formatDateTime(startDate),
                              endDate: formatDateTime(endDate),
                              handleStartDateChange,
                              handleEndDateChange,
                            }}
                          />
                        )}

                        {category !== Category.Update && (
                          <Grid item xs={12}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend">Status</FormLabel>
                              {category === Category.Maintenance ? (
                                <RadioGroup
                                  row
                                  id="status"
                                  name="maintenance"
                                  color="default"
                                  value={status}
                                  onChange={handleStatusChange}
                                >
                                  <FormControlLabel
                                    value="announcement"
                                    control={<Radio />}
                                    label="Announcement"
                                  />
                                  <FormControlLabel
                                    value="started"
                                    control={<Radio />}
                                    label="Started"
                                  />
                                  <FormControlLabel
                                    value="completed"
                                    control={<Radio />}
                                    label="Completed"
                                  />
                                </RadioGroup>
                              ) : (
                                <RadioGroup
                                  row
                                  id="status"
                                  name="incident"
                                  color="default"
                                  value={status}
                                  onChange={handleStatusChange}
                                >
                                  <FormControlLabel
                                    value={Status.Up}
                                    control={<Radio />}
                                    label="Up"
                                  />
                                  <FormControlLabel
                                    value={Status.Down}
                                    control={<Radio />}
                                    label="Down"
                                  />
                                  <FormControlLabel
                                    value={Status.Degraded}
                                    control={<Radio />}
                                    label="Degraded"
                                  />
                                </RadioGroup>
                              )}
                            </FormControl>
                          </Grid>
                        )}

                        {shouldStatusIOCheckboxBeDisplayed && (
                          <Grid item xs={12}>
                            <Typography gutterBottom variant="body1">
                              Additional Channels:
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={statusIoNotification}
                                  onChange={handleStatusIOCheckboxChange}
                                />
                              }
                              label="status.io"
                            />
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <PreviewNotificationComponent
                            data={{
                              category,
                              name,
                              status,
                              title,
                              authorName,
                              description,
                              instance,
                              startDate,
                              endDate,
                              dateFns,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gridGap={8}
                          >
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<ExitToAppIcon />}
                              onClick={goBack}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              type="submit"
                              startIcon={<SendIcon />}
                            >
                              Send notification
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6}>
              <GettingStartedComponent />
            </Grid>
          </Grid>
        </Content>
      </Page>
    </RequirePermission>
  );
};
